import React, { useCallback, useEffect, useRef } from 'react';
import GridElement from './GridElement';
import AudioPlayer from './AudioPlayer';
import VideoPlayer from './VideoPlayer';
import { useExerciseSync } from '../context/ExerciseSyncContext';
import ABCDExerciseBoard from './Exercise/ABCDExerciseBoard';
import WordDefinitionExerciseBoard from './Exercise/WordDefinitionExerciseBoard';
import MatchingHalvesExerciseBoard from './Exercise/MatchingHalvesExerciseBoard';
import FillInTheGapsExerciseBoard from './Exercise/FillInTheGapsExerciseBoard';
import TrueFalseExerciseBoard from './Exercise/TrueFalseExerciseBoard';
import WordTranslationExerciseBoard from './Exercise/WordTranslationExerciseBoard';
import ErrorCorrectionExerciseBoard from './Exercise/ErrorCorrectionExerciseBoard';
import WarmUpDiscussionExerciseBoard from './Exercise/WarmUpDiscussionExerciseBoard';
import CreateDialogueExerciseBoard from './Exercise/CreateDialogueExerciseBoard';
import EssentialVocabularyExerciseBoard from './Exercise/EssentialVocabularyExerciseBoard';
import LeadInActivitiesExerciseBoard from './Exercise/LeadInActivitiesExerciseBoard';
import FourOpinionsExerciseBoard from './Exercise/FourOpinionsExerciseBoard';
import CommunicativeSituationsExerciseBoard from './Exercise/CommunicativeSituationsExerciseBoard';
import ProsConsExerciseBoard from './Exercise/ProsConsExerciseBoard';
import GrammarRulesExerciseBoard from './Exercise/GrammarRulesExerciseBoard';
import JumbledSentenceExerciseBoard from './Exercise/JumbledSentenceExerciseBoard';
import MultipleChoiceExerciseBoard from './Exercise/MultipleChoiceExerciseBoard';
import OpenQuestionsExerciseBoard from './Exercise/OpenQuestionsExerciseBoard';
import QuizExerciseBoard from './Exercise/QuizExerciseBoard';
import PDFViewer from './PDFViewer';
import YouTubePlayer from './YouTubePlayer';
import TextElement from './TextElement';
import { createSmoothPath } from '../utils/whiteboardUtils';

const ExerciseBoardWrapper = React.memo(function ExerciseBoardWrapper({
  s, isStudent, isSelected, setSelectedShape, setShapes, centerOnExercise,
}) {
  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(String(s.id));
  // Expand/collapse lives on the shape itself (persisted through setShapes ->
  // whiteboardContents in LessonWorkspace) instead of local useState, so it
  // survives MiroLikeWhiteboard fully unmounting/remounting on every tab
  // switch.
  const isExpanded = !!s.isExpanded;

  const setIsExpanded = useCallback((next) => {
    setShapes(prev => prev.map(shape =>
      shape.id === s.id ? { ...shape, isExpanded: next } : shape
    ));
  }, [s.id, setShapes]);

  // Mirror a REMOTE peer's expand/collapse live. otherState.expanded itself
  // is seeded from localStorage and can carry a leftover value from a much
  // earlier session with no live peer connected at all — and since this
  // wrapper remounts fresh on every tab switch, naively re-applying
  // otherState.expanded on every mount replayed that stale value over
  // whatever the user had just set locally, making shapes look like they
  // randomly "flipped". liveUpdateCount only increments when a genuine
  // realtime broadcast is received (tracked on ExerciseSyncProvider, which
  // — unlike this wrapper — never unmounts on tab switch), so comparing
  // against its value at mount time tells apart "a peer just did something"
  // from "this is just replayed old data" even though this component itself
  // has no memory across remounts.
  const lastLiveUpdateCountRef = useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
    updateState({ expanded: true });
    if (setSelectedShape) setSelectedShape(s.id);
    if (centerOnExercise) centerOnExercise(s);
  }, [s, setSelectedShape, centerOnExercise, updateState, setIsExpanded]);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    updateState({ expanded: false });
  }, [updateState, setIsExpanded]);

  const handleUpdate = useCallback((updatedData) => {
    setShapes(prev => prev.map(shape =>
      shape.id === s.id ? { ...shape, exerciseData: updatedData } : shape
    ));
  }, [s.id, setShapes]);

  const boardProps = {
    exerciseData: s.exerciseData,
    exerciseType: s.exerciseType,
    isSelected,
    onSelect: () => setSelectedShape(s.id),
    onExpand: handleExpand,
    onClose: handleClose,
    isExpanded,
    isStudent,
    myState,
    otherState,
    updateState,
    onUpdate: handleUpdate,
  };

  const type = s.exerciseType;
  if (type === 'worddefinition') return <WordDefinitionExerciseBoard {...boardProps} />;
  if (type === 'matchinghalves') return <MatchingHalvesExerciseBoard {...boardProps} />;
  if (type === 'fillinthegaps') return <FillInTheGapsExerciseBoard {...boardProps} />;
  if (type === 'truefalse') return <TrueFalseExerciseBoard {...boardProps} />;
  if (type === 'wordtranslation') return <WordTranslationExerciseBoard {...boardProps} />;
  if (type === 'errorcorrection') return <ErrorCorrectionExerciseBoard {...boardProps} />;
  if (type === 'warmupdiscussion') return <WarmUpDiscussionExerciseBoard {...boardProps} />;
  if (type === 'createdialogue') return <CreateDialogueExerciseBoard {...boardProps} />;
  if (type === 'essentialvocabulary') return <EssentialVocabularyExerciseBoard {...boardProps} />;
  if (type === 'leadinactivities') return <LeadInActivitiesExerciseBoard {...boardProps} />;
  if (type === 'fouropinions') return <FourOpinionsExerciseBoard {...boardProps} />;
  if (type === 'communicativesituations') return <CommunicativeSituationsExerciseBoard {...boardProps} />;
  if (type === 'proscons') return <ProsConsExerciseBoard {...boardProps} />;
  if (type === 'grammarrules') return <GrammarRulesExerciseBoard {...boardProps} />;
  if (type === 'jumbledsentence') return <JumbledSentenceExerciseBoard {...boardProps} />;
  if (type === 'multiplechoice') return <MultipleChoiceExerciseBoard {...boardProps} />;
  if (type === 'openquestions') return <OpenQuestionsExerciseBoard {...boardProps} />;
  if (type === 'quiz') return <QuizExerciseBoard {...boardProps} />;
  return <ABCDExerciseBoard {...boardProps} />;
});

const WhiteboardShapeList = ({
  shapes, selectedShape, setSelectedShape, tool, isViewOnly, isStudent,
  getPointerCoords, panRef, svgRef, setDragged, fadedShapes,
  setShapeToolbar, setShapes, handleYouTubeClick, pan,
  editingStickerId, editingTextId, setEditingStickerId, setEditingTextId,
  setIsResizing, setOriginalSize, setResizeStart, setResizing,
  setStartShapes, setTextSizes, stickerEditRef, draggedSticker,
  textSizes, centerOnExercise,
}) => {
  return (
    <>
            {/* Render existing shapes */}
            {shapes.map(s => {
              const isSelected = s.id === selectedShape;
              if (s.type === 'rect') {
                return (
                  <g 
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen' || isViewOnly) return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                    onTouchStart={(e) => {
                      if (e.touches.length > 1) return; // Пропускаем pinch zoom
                      if (tool === 'pen' || isViewOnly) return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                  >
                    <rect 
                      x={s.x} 
                      y={s.y} 
                      width={s.w || s.width} 
                      height={s.h || s.height} 
                      fill={s.fill || 'none'} 
                      stroke={s.color || '#000'} 
                      strokeWidth={s.strokeStyle === 'no stroke' ? 0 : (s.strokeWidth || 1)} 
                      rx={s.borderRadius !== undefined ? s.borderRadius : 12} 
                      opacity={fadedShapes.has(s.id) ? 0.3 : (s.opacity !== undefined ? s.opacity : 1)}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                    />
                    {isSelected && (
                      <>
                        <rect
                          x={s.x - 8}
                          y={s.y - 8}
                          width={(s.w || s.width) + 16}
                          height={(s.h || s.height) + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: s.x - 8, y: s.y - 8, cursor: 'nwse-resize' },
                          { x: s.x + (s.w || s.width) + 8, y: s.y - 8, cursor: 'nesw-resize' },
                          { x: s.x - 8, y: s.y + (s.h || s.height) + 8, cursor: 'nesw-resize' },
                          { x: s.x + (s.w || s.width) + 8, y: s.y + (s.h || s.height) + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                              setOriginalSize({ width: s.w || s.width, height: s.h || s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                        
                        {/* Shape toolbar button */}
                        <rect
                          x={s.x + (s.w || s.width) / 2 - 16 / pan.scale}
                          y={s.y - 40 / pan.scale}
                          width={32 / pan.scale}
                          height={24 / pan.scale}
                          rx={6 / pan.scale}
                          fill="#fff"
                          stroke="#e0e0e0"
                          strokeWidth={1 / pan.scale}
                          style={{ 
                            cursor: 'pointer',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const rect = svgRef.current.getBoundingClientRect();
                            const x = s.x + (s.w || s.width) / 2;
                            const y = s.y;
                            const screenX = x * pan.scale + pan.x + rect.left;
                            const screenY = y * pan.scale + pan.y + rect.top;
                            setShapeToolbar({ shape: s, x: screenX, y: screenY });
                          }}
                        />
                        <svg
                          x={s.x + (s.w || s.width) / 2 - 6 / pan.scale}
                          y={s.y - 34 / pan.scale}
                          width={12 / pan.scale}
                          height={12 / pan.scale}
                          viewBox="0 0 100 100"
                          fill="#666"
                          style={{ pointerEvents: 'none' }}
                        >
                          <path d="m80 95h-60c-8.271 0-15-6.729-15-15v-60c0-8.271 6.729-15 15-15h27c1.657 0 3 1.342 3 3s-1.343 3-3 3h-27c-4.963 0-9 4.037-9 9v60c0 4.963 4.037 9 9 9h60c4.963 0 9-4.037 9-9v-27c0-1.658 1.343-3 3-3s3 1.342 3 3v27c0 8.271-6.729 15-15 15zm15-73.649c0 3.905-1.403 7.298-3.954 9.847l-34.167 34.165c-1.699 1.699-3.961 2.637-6.365 2.637h-15.514c-1.657 0-3-1.342-3-3v-15.516c0-2.402.936-4.664 2.637-6.363l34.167-34.166c2.55-2.552 5.94-3.955 9.545-3.955h.302c3.605 0 6.995 1.403 9.545 3.955l2.849 2.848c2.552 2.549 3.955 5.941 3.955 9.548zm-16.242 13.65-13.757-13.758-26.122 26.12c-.558.56-.879 1.333-.879 2.121v12.516h12.514c.801 0 1.556-.313 2.123-.879zm10.242-13.352c0-2.303-.779-4.189-2.196-5.604l-2.849-2.848c-1.419-1.418-3.302-2.197-5.304-2.197h-.302c-2.002 0-3.885.779-5.303 2.197l-3.803 3.803 13.758 13.758 3.803-3.803c1.417-1.415 2.196-3.302 2.196-5.306z"/>
                        </svg>
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'circle') {
                return (
                  <g 
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                    onTouchStart={(e) => {
                      if (tool === 'pen') return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                  >
                    {s.rx && s.ry ? (
                      <ellipse 
                        cx={s.x} 
                        cy={s.y} 
                        rx={s.rx} 
                        ry={s.ry} 
                        fill={s.fill || 'none'} 
                        stroke={s.color || '#000'} 
                        strokeWidth={s.strokeStyle === 'no stroke' ? 0 : (s.strokeWidth || 1)} 
                        opacity={fadedShapes.has(s.id) ? 0.3 : (s.opacity !== undefined ? s.opacity : 1)}
                        strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                      />
                    ) : (
                    <circle 
                      cx={s.x} 
                      cy={s.y} 
                      r={s.r || (s.width/2)} 
                      fill={s.fill || 'none'} 
                      stroke={s.color || '#000'} 
                      strokeWidth={s.strokeStyle === 'no stroke' ? 0 : (s.strokeWidth || 1)} 
                      opacity={fadedShapes.has(s.id) ? 0.3 : (s.opacity !== undefined ? s.opacity : 1)}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                    />
                    )}
                    {isSelected && (
                      <>
                        <rect
                          x={s.x - (s.rx || s.r || (s.width/2)) - 8}
                          y={s.y - (s.ry || s.r || (s.width/2)) - 8}
                          width={2 * (s.rx || s.r || (s.width/2)) + 16}
                          height={2 * (s.ry || s.r || (s.width/2)) + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: s.x - (s.rx || s.r || (s.width/2)) - 8, y: s.y - (s.ry || s.r || (s.width/2)) - 8, cursor: 'nwse-resize' },
                          { x: s.x + (s.rx || s.r || (s.width/2)) + 8, y: s.y - (s.ry || s.r || (s.width/2)) - 8, cursor: 'nesw-resize' },
                          { x: s.x - (s.rx || s.r || (s.width/2)) - 8, y: s.y + (s.ry || s.r || (s.width/2)) + 8, cursor: 'nesw-resize' },
                          { x: s.x + (s.rx || s.r || (s.width/2)) + 8, y: s.y + (s.ry || s.r || (s.width/2)) + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                              setOriginalSize({ width: s.r || (s.width/2), height: s.r || (s.width/2), r: s.r || (s.width/2) });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                        
                        {/* Shape toolbar button */}
                        <rect
                          x={s.x - 16 / pan.scale}
                          y={s.y - (s.ry || s.r || (s.width/2)) - 40 / pan.scale}
                          width={32 / pan.scale}
                          height={24 / pan.scale}
                          rx={6 / pan.scale}
                          fill="#fff"
                          stroke="#e0e0e0"
                          strokeWidth={1 / pan.scale}
                          style={{ 
                            cursor: 'pointer',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const rect = svgRef.current.getBoundingClientRect();
                            const x = s.x;
                            const y = s.y - (s.ry || s.r || (s.width/2));
                            const screenX = x * pan.scale + pan.x + rect.left;
                            const screenY = y * pan.scale + pan.y + rect.top;
                            setShapeToolbar({ shape: s, x: screenX, y: screenY });
                          }}
                        />
                        <svg
                          x={s.x - 6 / pan.scale}
                          y={s.y - (s.ry || s.r || (s.width/2)) - 34 / pan.scale}
                          width={12 / pan.scale}
                          height={12 / pan.scale}
                          viewBox="0 0 100 100"
                          fill="#666"
                          style={{
                            pointerEvents: 'none'
                          }}
                        >
                          <path d="m80 95h-60c-8.271 0-15-6.729-15-15v-60c0-8.271 6.729-15 15-15h27c1.657 0 3 1.342 3 3s-1.343 3-3 3h-27c-4.963 0-9 4.037-9 9v60c0 4.963 4.037 9 9 9h60c4.963 0 9-4.037 9-9v-27c0-1.658 1.343-3 3-3s3 1.342 3 3v27c0 8.271-6.729 15-15 15zm15-73.649c0 3.905-1.403 7.298-3.954 9.847l-34.167 34.165c-1.699 1.699-3.961 2.637-6.365 2.637h-15.514c-1.657 0-3-1.342-3-3v-15.516c0-2.402.936-4.664 2.637-6.363l34.167-34.166c2.55-2.552 5.94-3.955 9.545-3.955h.302c3.605 0 6.995 1.403 9.545 3.955l2.849 2.848c2.552 2.549 3.955 5.941 3.955 9.548zm-16.242 13.65-13.757-13.758-26.122 26.12c-.558.56-.879 1.333-.879 2.121v12.516h12.514c.801 0 1.556-.313 2.123-.879zm10.242-13.352c0-2.303-.779-4.189-2.196-5.604l-2.849-2.848c-1.419-1.418-3.302-2.197-5.304-2.197h-.302c-2.002 0-3.885.779-5.303 2.197l-3.803 3.803 13.758 13.758 3.803-3.803c1.417-1.415 2.196-3.302 2.196-5.306z"/>
                        </svg>
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'triangle') {
                const p1 = [s.x + (s.w || s.width)/2, s.y];
                const p2 = [s.x + (s.w || s.width), s.y + (s.h || s.height)];
                const p3 = [s.x, s.y + (s.h || s.height)];
                return (
                  <g 
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return; // Не захватываем элементы при рисовании
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                  >
                    <polygon
                      points={`${p1.join(',')} ${p2.join(',')} ${p3.join(',')}`} 
                      fill={s.fill || 'none'} 
                      stroke={s.color || '#000'} 
                      strokeWidth={s.strokeWidth || 1} 
                      opacity={fadedShapes.has(s.id) ? 0.3 : (s.opacity !== undefined ? s.opacity : 1)}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : s.strokeStyle === 'double' ? '0,4' : undefined}
                      strokeDashoffset={s.strokeStyle === 'double' ? '2' : undefined}
                    />
                    {isSelected && (
                      <>
                        <rect
                          x={s.x - 8}
                          y={s.y - 8}
                          width={(s.w || s.width) + 16}
                          height={(s.h || s.height) + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: s.x - 8, y: s.y - 8, cursor: 'nwse-resize' },
                          { x: s.x + (s.w || s.width) + 8, y: s.y - 8, cursor: 'nesw-resize' },
                          { x: s.x - 8, y: s.y + (s.h || s.height) + 8, cursor: 'nesw-resize' },
                          { x: s.x + (s.w || s.width) + 8, y: s.y + (s.h || s.height) + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                              setOriginalSize({ width: s.w || s.width, height: s.h || s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                        
                        {/* Shape toolbar button */}
                        <rect
                          x={s.x + (s.w || s.width) / 2 - 16 / pan.scale}
                          y={s.y - 40 / pan.scale}
                          width={32 / pan.scale}
                          height={24 / pan.scale}
                          rx={6 / pan.scale}
                          fill="#fff"
                          stroke="#e0e0e0"
                          strokeWidth={1 / pan.scale}
                          style={{ 
                            cursor: 'pointer',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const rect = svgRef.current.getBoundingClientRect();
                            const x = s.x + (s.w || s.width) / 2;
                            const y = s.y;
                            const screenX = x * pan.scale + pan.x + rect.left;
                            const screenY = y * pan.scale + pan.y + rect.top;
                            setShapeToolbar({ shape: s, x: screenX, y: screenY });
                          }}
                        />
                        <svg
                          x={s.x + (s.w || s.width) / 2 - 6 / pan.scale}
                          y={s.y - 34 / pan.scale}
                          width={12 / pan.scale}
                          height={12 / pan.scale}
                          viewBox="0 0 100 100"
                          fill="#666"
                          style={{
                            pointerEvents: 'none'
                          }}
                        >
                          <path d="m80 95h-60c-8.271 0-15-6.729-15-15v-60c0-8.271 6.729-15 15-15h27c1.657 0 3 1.342 3 3s-1.343 3-3 3h-27c-4.963 0-9 4.037-9 9v60c0 4.963 4.037 9 9 9h60c4.963 0 9-4.037 9-9v-27c0-1.658 1.343-3 3-3s3 1.342 3 3v27c0 8.271-6.729 15-15 15zm15-73.649c0 3.905-1.403 7.298-3.954 9.847l-34.167 34.165c-1.699 1.699-3.961 2.637-6.365 2.637h-15.514c-1.657 0-3-1.342-3-3v-15.516c0-2.402.936-4.664 2.637-6.363l34.167-34.166c2.55-2.552 5.94-3.955 9.545-3.955h.302c3.605 0 6.995 1.403 9.545 3.955l2.849 2.848c2.552 2.549 3.955 5.941 3.955 9.548zm-16.242 13.65-13.757-13.758-26.122 26.12c-.558.56-.879 1.333-.879 2.121v12.516h12.514c.801 0 1.556-.313 2.123-.879zm10.242-13.352c0-2.303-.779-4.189-2.196-5.604l-2.849-2.848c-1.419-1.418-3.302-2.197-5.304-2.197h-.302c-2.002 0-3.885.779-5.303 2.197l-3.803 3.803 13.758 13.758 3.803-3.803c1.417-1.415 2.196-3.302 2.196-5.306z"/>
                        </svg>
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'arrow') {
                if (!Number.isFinite(s.x1) || !Number.isFinite(s.y1) || !Number.isFinite(s.x2) || !Number.isFinite(s.y2)) {
                  return null;
                }
                const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
                const angle = Math.atan2(dy, dx);
                const headlen = 18;
                const arrowX = s.x2 - headlen * Math.cos(angle);
                const arrowY = s.y2 - headlen * Math.sin(angle);
                
                return (
                  <g 
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return; // Не захватываем элементы при рисовании
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);

                      // Для стрелок включаем сегментный drag: записываем только последнюю позицию курсора
                      setDragged({ id: s.id, type: 'segment', lastX: x, lastY: y });
                    }}
                  >
                    {/* Invisible wide hit area for body drag */}
                    <line
                      x1={s.x1}
                      y1={s.y1}
                      x2={s.x2}
                      y2={s.y2}
                      stroke="transparent"
                      strokeWidth={Math.max(16, (s.strokeWidth || s.thickness || 1) + 12)}
                      style={{ cursor: 'move' }}
                    />
                    <line
                      x1={s.x1}
                      y1={s.y1}
                      x2={arrowX}
                      y2={arrowY}
                      stroke={s.color || '#000'}
                      strokeWidth={s.strokeWidth || s.thickness || 1}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                      opacity={s.opacity !== undefined ? s.opacity : 1}
                    />
                    <polygon 
                      points={`
                        ${s.x2},${s.y2}
                        ${arrowX + 7*Math.sin(angle)},${arrowY - 7*Math.cos(angle)}
                        ${arrowX - 7*Math.sin(angle)},${arrowY + 7*Math.cos(angle)}
                      `} 
                      fill={s.color || '#000'}
                      opacity={s.opacity !== undefined ? s.opacity : 1}
                    />
                    {isSelected && (
                      <>
                        {/* Простые круглые точки на концах стрелки */}
                        <circle
                          cx={s.x1}
                          cy={s.y1}
                          r={6}
                          fill="#fff"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ cursor: 'move' }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                            setIsResizing(true);
                            setResizeStart({ x, y, end: 'start' });
                            setOriginalSize({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2 });
                            setStartShapes([s]);
                          }}
                        />
                          <circle
                          cx={s.x2}
                          cy={s.y2}
                          r={6}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2}
                          style={{ cursor: 'move' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                            setResizeStart({ x, y, end: 'end' });
                            setOriginalSize({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2 });
                              setStartShapes([s]);
                            }}
                          />
                        
                        {/* Shape toolbar button */}
                        <rect
                          x={(s.x1 + s.x2) / 2 - 16 / pan.scale}
                          y={(s.y1 + s.y2) / 2 - 40 / pan.scale}
                          width={32 / pan.scale}
                          height={24 / pan.scale}
                          rx={6 / pan.scale}
                          fill="#fff"
                          stroke="#e0e0e0"
                          strokeWidth={1 / pan.scale}
                          style={{ 
                            cursor: 'pointer',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const rect = svgRef.current.getBoundingClientRect();
                            const x = (s.x1 + s.x2) / 2;
                            const y = (s.y1 + s.y2) / 2;
                            const screenX = x * pan.scale + pan.x + rect.left;
                            const screenY = y * pan.scale + pan.y + rect.top;
                            setShapeToolbar({ shape: s, x: screenX, y: screenY });
                          }}
                        />
                        <svg
                          x={(s.x1 + s.x2) / 2 - 6 / pan.scale}
                          y={(s.y1 + s.y2) / 2 - 34 / pan.scale}
                          width={12 / pan.scale}
                          height={12 / pan.scale}
                          viewBox="0 0 100 100"
                          fill="#666"
                          style={{
                            pointerEvents: 'none'
                          }}
                        >
                          <path d="m80 95h-60c-8.271 0-15-6.729-15-15v-60c0-8.271 6.729-15 15-15h27c1.657 0 3 1.342 3 3s-1.343 3-3 3h-27c-4.963 0-9 4.037-9 9v60c0 4.963 4.037 9 9 9h60c4.963 0 9-4.037 9-9v-27c0-1.658 1.343-3 3-3s3 1.342 3 3v27c0 8.271-6.729 15-15 15zm15-73.649c0 3.905-1.403 7.298-3.954 9.847l-34.167 34.165c-1.699 1.699-3.961 2.637-6.365 2.637h-15.514c-1.657 0-3-1.342-3-3v-15.516c0-2.402.936-4.664 2.637-6.363l34.167-34.166c2.55-2.552 5.94-3.955 9.545-3.955h.302c3.605 0 6.995 1.403 9.545 3.955l2.849 2.848c2.552 2.549 3.955 5.941 3.955 9.548zm-16.242 13.65-13.757-13.758-26.122 26.12c-.558.56-.879 1.333-.879 2.121v12.516h12.514c.801 0 1.556-.313 2.123-.879zm10.242-13.352c0-2.303-.779-4.189-2.196-5.604l-2.849-2.848c-1.419-1.418-3.302-2.197-5.304-2.197h-.302c-2.002 0-3.885.779-5.303 2.197l-3.803 3.803 13.758 13.758 3.803-3.803c1.417-1.415 2.196-3.302 2.196-5.306z"/>
                        </svg>
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'line') {
                if (!Number.isFinite(s.x1) || !Number.isFinite(s.y1) || !Number.isFinite(s.x2) || !Number.isFinite(s.y2)) {
                  return null;
                }
                return (
                  <g
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return; // Не захватываем элементы при рисовании
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);

                      // Линии тоже используем сегментный drag
                      setDragged({ id: s.id, type: 'segment', lastX: x, lastY: y });
                    }}
                  >
                    {/* Invisible wide hit area for body drag */}
                    <line
                      x1={s.x1}
                      y1={s.y1}
                      x2={s.x2}
                      y2={s.y2}
                      stroke="transparent"
                      strokeWidth={Math.max(16, (s.strokeWidth || s.thickness || 1) + 12)}
                      style={{ cursor: 'move' }}
                    />
                    <line
                      x1={s.x1}
                      y1={s.y1}
                      x2={s.x2}
                      y2={s.y2}
                      stroke={s.color || '#000'}
                      strokeWidth={s.strokeWidth || s.thickness || 1}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                      opacity={s.opacity !== undefined ? s.opacity : 1}
                    />

                    {/* Рендеринг левого конца линии */}
                    {s.startEndStyle === 'arrow' && (() => {
                      const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
                      const angle = Math.atan2(dy, dx);
                      const arrowLength = 8;
                      const arrowWidth = 4;
                      const arrowX = s.x1 + arrowLength * Math.cos(angle);
                      const arrowY = s.y1 + arrowLength * Math.sin(angle);
                      const perpX = arrowWidth * Math.cos(angle + Math.PI/2);
                      const perpY = arrowWidth * Math.sin(angle + Math.PI/2);
                      
                      return (
                        <polygon 
                          points={`${s.x1},${s.y1} ${arrowX + perpX},${arrowY + perpY} ${arrowX - perpX},${arrowY - perpY}`}
                          fill={s.color || '#000'}
                          stroke={s.color || '#000'}
                          strokeWidth={s.strokeWidth || s.thickness || 1}
                          opacity={s.opacity !== undefined ? s.opacity : 1}
                        />
                      );
                    })()}
                    {s.startEndStyle === 'circle' && (
                      <>
                        {/* Круг на левом конце линии */}
                        <circle 
                          cx={s.x1} 
                          cy={s.y1} 
                          r={4}
                          fill={s.color || '#000'}
                          stroke={s.color || '#000'}
                          strokeWidth={s.strokeWidth || s.thickness || 1}
                          opacity={s.opacity !== undefined ? s.opacity : 1}
                        />
                      </>
                    )}
                    {s.startEndStyle === 'square' && (
                      <>
                        {/* Квадрат на левом конце линии */}
                        <rect 
                          x={s.x1 - 4} 
                          y={s.y1 - 4} 
                          width={8} 
                          height={8}
                          fill={s.color || '#000'}
                          stroke={s.color || '#000'}
                          strokeWidth={s.strokeWidth || s.thickness || 1}
                          opacity={s.opacity !== undefined ? s.opacity : 1}
                        />
                      </>
                    )}

                    {/* Рендеринг правого конца линии */}
                    {s.endEndStyle === 'arrow' && (() => {
                      const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
                      const angle = Math.atan2(dy, dx);
                      const arrowLength = 8;
                      const arrowWidth = 4;
                      const arrowX = s.x2 - arrowLength * Math.cos(angle);
                      const arrowY = s.y2 - arrowLength * Math.sin(angle);
                      const perpX = arrowWidth * Math.cos(angle + Math.PI/2);
                      const perpY = arrowWidth * Math.sin(angle + Math.PI/2);
                      
                      return (
                        <polygon 
                          points={`${s.x2},${s.y2} ${arrowX + perpX},${arrowY + perpY} ${arrowX - perpX},${arrowY - perpY}`}
                          fill={s.color || '#000'}
                          stroke={s.color || '#000'}
                          strokeWidth={s.strokeWidth || s.thickness || 1}
                          opacity={s.opacity !== undefined ? s.opacity : 1}
                        />
                      );
                    })()}
                    {s.endEndStyle === 'circle' && (
                      <>
                        {/* Круг на правом конце линии */}
                        <circle 
                          cx={s.x2} 
                          cy={s.y2} 
                          r={4}
                          fill={s.color || '#000'}
                          stroke={s.color || '#000'}
                          strokeWidth={s.strokeWidth || s.thickness || 1}
                          opacity={s.opacity !== undefined ? s.opacity : 1}
                        />
                      </>
                    )}
                    {s.endEndStyle === 'square' && (
                      <>
                        {/* Квадрат на правом конце линии */}
                        <rect 
                          x={s.x2 - 4} 
                          y={s.y2 - 4} 
                          width={8} 
                          height={8}
                          fill={s.color || '#000'}
                          stroke={s.color || '#000'}
                          strokeWidth={s.strokeWidth || s.thickness || 1}
                          opacity={s.opacity !== undefined ? s.opacity : 1}
                        />
                      </>
                    )}
                    {isSelected && (
                      <>
                        {/* Простые круглые точки на концах линии */}
                        <circle
                          cx={s.x1}
                          cy={s.y1}
                          r={6}
                          fill="#fff"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ cursor: 'move' }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                            setIsResizing(true);
                            setResizeStart({ x, y, end: 'start' });
                            setOriginalSize({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2 });
                            setStartShapes([s]);
                          }}
                        />
                          <circle
                          cx={s.x2}
                          cy={s.y2}
                          r={6}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2}
                          style={{ cursor: 'move' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                            setResizeStart({ x, y, end: 'end' });
                            setOriginalSize({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2 });
                              setStartShapes([s]);
                            }}
                          />
                          
                          {/* Shape toolbar button */}
                          <rect
                            x={(s.x1 + s.x2) / 2 - 16 / pan.scale}
                            y={(s.y1 + s.y2) / 2 - 40 / pan.scale}
                            width={32 / pan.scale}
                            height={24 / pan.scale}
                            rx={6 / pan.scale}
                            fill="#fff"
                            stroke="#e0e0e0"
                            strokeWidth={1 / pan.scale}
                            style={{ 
                              cursor: 'pointer',
                              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const rect = svgRef.current.getBoundingClientRect();
                              const x = (s.x1 + s.x2) / 2;
                              const y = (s.y1 + s.y2) / 2;
                              const screenX = x * pan.scale + pan.x + rect.left;
                              const screenY = y * pan.scale + pan.y + rect.top;
                              setShapeToolbar({ shape: s, x: screenX, y: screenY });
                            }}
                          />
                          <svg
                            x={(s.x1 + s.x2) / 2 - 6 / pan.scale}
                            y={(s.y1 + s.y2) / 2 - 34 / pan.scale}
                            width={12 / pan.scale}
                            height={12 / pan.scale}
                            viewBox="0 0 100 100"
                            fill="#666"
                            style={{
                              pointerEvents: 'none'
                            }}
                          >
                            <path d="m80 95h-60c-8.271 0-15-6.729-15-15v-60c0-8.271 6.729-15 15-15h27c1.657 0 3 1.342 3 3s-1.343 3-3 3h-27c-4.963 0-9 4.037-9 9v60c0 4.963 4.037 9 9 9h60c4.963 0 9-4.037 9-9v-27c0-1.658 1.343-3 3-3s3 1.342 3 3v27c0 8.271-6.729 15-15 15zm15-73.649c0 3.905-1.403 7.298-3.954 9.847l-34.167 34.165c-1.699 1.699-3.961 2.637-6.365 2.637h-15.514c-1.657 0-3-1.342-3-3v-15.516c0-2.402.936-4.664 2.637-6.363l34.167-34.166c2.55-2.552 5.94-3.955 9.545-3.955h.302c3.605 0 6.995 1.403 9.545 3.955l2.849 2.848c2.552 2.549 3.955 5.941 3.955 9.548zm-16.242 13.65-13.757-13.758-26.122 26.12c-.558.56-.879 1.333-.879 2.121v12.516h12.514c.801 0 1.556-.313 2.123-.879zm10.242-13.352c0-2.303-.779-4.189-2.196-5.604l-2.849-2.848c-1.419-1.418-3.302-2.197-5.304-2.197h-.302c-2.002 0-3.885.779-5.303 2.197l-3.803 3.803 13.758 13.758 3.803-3.803c1.417-1.415 2.196-3.302 2.196-5.306z"/>
                          </svg>
                        </>
                      )}
                    </g>
                  );
                }
                if (s.type === 'message') {
                return (
                  <g 
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                    onTouchStart={(e) => {
                      if (tool === 'pen') return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                  >
                    <rect 
                      x={s.x} 
                      y={s.y} 
                      width={s.width} 
                      height={s.height} 
                      rx={14} 
                      fill={s.fill || 'none'} 
                      stroke={s.color || '#000'} 
                      strokeWidth={s.strokeStyle === 'no stroke' ? 0 : (s.strokeWidth || 1)} 
                      opacity={fadedShapes.has(s.id) ? 0.3 : (s.opacity !== undefined ? s.opacity : 1)}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                    />
                    <polygon 
                      points={`${s.x + 24},${s.y + s.height} ${s.x + 44},${s.y + s.height} ${s.x + 34},${s.y + s.height + 18}`} 
                      fill={s.fill || 'none'} 
                      stroke={s.color || '#000'} 
                      strokeWidth={s.strokeStyle === 'no stroke' ? 0 : (s.strokeWidth || 1)} 
                      opacity={fadedShapes.has(s.id) ? 0.3 : (s.opacity !== undefined ? s.opacity : 1)}
                      strokeDasharray={s.strokeStyle === 'dashed' ? '8,4' : s.strokeStyle === 'dotted' ? '2,2' : undefined}
                    />
                    {isSelected && (
                      <>
                        <rect
                          x={s.x - 8}
                          y={s.y - 8}
                          width={s.width + 16}
                          height={s.height + 18 + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: s.x - 8, y: s.y - 8, cursor: 'nwse-resize' },
                          { x: s.x + s.width + 8, y: s.y - 8, cursor: 'nesw-resize' },
                          { x: s.x - 8, y: s.y + s.height + 18 + 8, cursor: 'nesw-resize' },
                          { x: s.x + s.width + 8, y: s.y + s.height + 18 + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                              setOriginalSize({ width: s.width, height: s.height + 18 });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                        
                        {/* Shape toolbar button */}
                        <rect
                          x={s.x + s.width / 2 - 16 / pan.scale}
                          y={s.y - 40 / pan.scale}
                          width={32 / pan.scale}
                          height={24 / pan.scale}
                          rx={6 / pan.scale}
                          fill="#fff"
                          stroke="#e0e0e0"
                          strokeWidth={1 / pan.scale}
                          style={{ 
                            cursor: 'pointer',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const rect = svgRef.current.getBoundingClientRect();
                            const x = s.x + s.width / 2;
                            const y = s.y;
                            const screenX = x * pan.scale + pan.x + rect.left;
                            const screenY = y * pan.scale + pan.y + rect.top;
                            setShapeToolbar({ shape: s, x: screenX, y: screenY });
                          }}
                        />
                        <svg
                          x={s.x + s.width / 2 - 6 / pan.scale}
                          y={s.y - 34 / pan.scale}
                          width={12 / pan.scale}
                          height={12 / pan.scale}
                          viewBox="0 0 100 100"
                          fill="#666"
                          style={{
                            pointerEvents: 'none'
                          }}
                        >
                          <path d="m80 95h-60c-8.271 0-15-6.729-15-15v-60c0-8.271 6.729-15 15-15h27c1.657 0 3 1.342 3 3s-1.343 3-3 3h-27c-4.963 0-9 4.037-9 9v60c0 4.963 4.037 9 9 9h60c4.963 0 9-4.037 9-9v-27c0-1.658 1.343-3 3-3s3 1.342 3 3v27c0 8.271-6.729 15-15 15zm15-73.649c0 3.905-1.403 7.298-3.954 9.847l-34.167 34.165c-1.699 1.699-3.961 2.637-6.365 2.637h-15.514c-1.657 0-3-1.342-3-3v-15.516c0-2.402.936-4.664 2.637-6.363l34.167-34.166c2.55-2.552 5.94-3.955 9.545-3.955h.302c3.605 0 6.995 1.403 9.545 3.955l2.849 2.848c2.552 2.549 3.955 5.941 3.955 9.548zm-16.242 13.65-13.757-13.758-26.122 26.12c-.558.56-.879 1.333-.879 2.121v12.516h12.514c.801 0 1.556-.313 2.123-.879zm10.242-13.352c0-2.303-.779-4.189-2.196-5.604l-2.849-2.848c-1.419-1.418-3.302-2.197-5.304-2.197h-.302c-2.002 0-3.885.779-5.303 2.197l-3.803 3.803 13.758 13.758 3.803-3.803c1.417-1.415 2.196-3.302 2.196-5.306z"/>
                        </svg>
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'polygon') {
                // Calculate bounding box for polygon
                const minX = Math.min(...s.points.map(p => p[0]));
                const maxX = Math.max(...s.points.map(p => p[0]));
                const minY = Math.min(...s.points.map(p => p[1]));
                const maxY = Math.max(...s.points.map(p => p[1]));
                const width = maxX - minX;
                const height = maxY - minY;
                
                return (
                  <g 
                    key={s.id}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return; // Не захватываем элементы при рисовании
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                  >
                    <polygon 
                      points={s.points.map(p => p.join(",")).join(' ')} 
                      fill={s.fill || 'none'} 
                      stroke={s.color || '#000'} 
                      strokeWidth={s.strokeWidth || 1} 
                    />
                    {isSelected && (
                      <>
                        <rect
                          x={minX - 8}
                          y={minY - 8}
                          width={width + 16}
                          height={height + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: minX - 8, y: minY - 8, cursor: 'nwse-resize' },
                          { x: maxX + 8, y: minY - 8, cursor: 'nesw-resize' },
                          { x: minX - 8, y: maxY + 8, cursor: 'nesw-resize' },
                          { x: maxX + 8, y: maxY + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y });
                              setOriginalSize({ width, height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'pencil') {
                const smoothPath = createSmoothPath(s.points, s.thickness);

                // Calculate bounding box for the pencil stroke
                const minX = Math.min(...s.points.map(p => p[0]));
                const maxX = Math.max(...s.points.map(p => p[0]));
                const minY = Math.min(...s.points.map(p => p[1]));
                const maxY = Math.max(...s.points.map(p => p[1]));
                const width = maxX - minX;
                const height = maxY - minY;

                return (
                  <g
                    key={s.id}
                    style={(tool === 'pen' || tool === 'highlighter') ? { pointerEvents: 'none' } : undefined}
                    onMouseDown={(e) => {
                      if (tool === 'pen' || tool === 'highlighter' || isViewOnly) return;
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, type: 'pencil', lastX: x, lastY: y });
                    }}
                  >
                    {/* Invisible wide stroke so thin lines are easy to click */}
                    <path
                      d={smoothPath}
                      fill="transparent"
                      stroke="transparent"
                      strokeWidth={Math.max((s.thickness || 4) + 12, 16)}
                      style={{ pointerEvents: 'stroke' }}
                    />
                    <path
                      d={smoothPath}
                      fill={s.color}
                      stroke="none"
                      opacity={fadedShapes.has(s.id) ? 0.3 : (s.alpha ?? 1)}
                      style={{ pointerEvents: 'none' }}
                    />
                    {isSelected && (
                      <>
                        <rect
                          x={minX - 8}
                          y={minY - 8}
                          width={width + 16}
                          height={height + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: minX - 8, y: minY - 8, cursor: 'nwse-resize', corner: 'nw' },
                          { x: maxX + 8, y: minY - 8, cursor: 'nesw-resize', corner: 'ne' },
                          { x: minX - 8, y: maxY + 8, cursor: 'nesw-resize', corner: 'sw' },
                          { x: maxX + 8, y: maxY + 8, cursor: 'nwse-resize', corner: 'se' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return;
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y, corner: corner.corner });
                              setOriginalSize({ minX, minY, width, height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'text') {
                return (
                                  <TextElement
                  key={s.id}
                  shape={s}
                  isSelected={s.id === selectedShape}
                  isEditing={editingTextId === s.id}
                  textSizes={textSizes}
                  pan={pan}
                  tool={tool}
                  editingTextId={editingTextId}
                  fadedShapes={fadedShapes}
                  onSelect={setSelectedShape}
                  onEditStart={setEditingTextId}
                  onEditEnd={(id) => setEditingTextId(null)}
                  onTextChange={(id, newText) => {
                    setShapes(prev => prev.map(sh => sh.id === id ? { ...sh, text: newText } : sh));
                  }}
                  onDragOffsetChange={() => {}}
                  onShapeUpdate={(id, updates) => {
                    setShapes(prev => prev.map(sh => sh.id === id ? { ...sh, ...updates } : sh));
                  }}
                  onResize={(id, updates) => {
                    setShapes(prev => prev.map(sh => sh.id === id ? { ...sh, ...updates } : sh));
                    setTextSizes(prev => ({
                      ...prev,
                      [id]: {
                        width: updates.width || prev[id]?.width || 200,
                        height: updates.height || prev[id]?.height || 64
                      }
                    }));
                    

                  }}
                  getPointerCoords={getPointerCoords}
                  svgRef={svgRef}
                  panRef={panRef}
                />
                );
              }
              
              if (s.type === 'grid') {
                return (
                  <g key={s.id}>
                    <GridElement
                      shape={s}
                      isSelected={s.id === selectedShape}
                      tool={tool}
                      fadedShapes={fadedShapes}
                      onSelect={setSelectedShape}
                      onShapeUpdate={(id, updates) => setShapes(prev => prev.map(sh => sh.id === id ? { ...sh, ...updates } : sh))}
                      getPointerCoords={getPointerCoords}
                      svgRef={svgRef}
                      panRef={panRef}
                    />
                  </g>
                );
              }

              if (s.type === 'exercise') {
                const isSelected = s.id === selectedShape;
                return (
                  <g key={s.id}>
                    <foreignObject 
                      x={s.x} 
                      y={s.y} 
                      width={s.width} 
                      height={s.height}
                      onMouseDown={(e) => {
                        if (tool === 'pen') return;
                        e.stopPropagation();
                        setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <div style={{ width: s.width, height: s.height }}>
                        <ExerciseBoardWrapper
                          s={s}
                          isStudent={isStudent}
                          isSelected={isSelected}
                          setSelectedShape={setSelectedShape}
                          setShapes={setShapes}
                          centerOnExercise={centerOnExercise}
                        />
                      </div>
                    </foreignObject>
                    
                    {/* Selection outline */}
                    {isSelected && (
                      <>
                        <rect
                          x={s.x - 8}
                          y={s.y - 8}
                          width={s.width + 16}
                          height={s.height + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles */}
                        {[
                          { x: s.x - 8, y: s.y - 8, cursor: 'nwse-resize' },
                          { x: s.x + s.width + 8, y: s.y - 8, cursor: 'nesw-resize' },
                          { x: s.x - 8, y: s.y + s.height + 8, cursor: 'nesw-resize' },
                          { x: s.x + s.width + 8, y: s.y + s.height + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return;
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y });
                              setOriginalSize({ width: s.width, height: s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              if (['image', 'video', 'audio', 'pdf', 'youtube', 'vimeo', 'file', 'link'].includes(s.type)) {
                const isSelected = s.id === selectedShape;
                if (s.type === 'image') {
                  return (
                    <g
                      key={s.id}
                      transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                      style={(tool === 'pen' || tool === 'highlighter') ? { pointerEvents: 'none' } : undefined}
                      onMouseDown={(e) => {
                        if (tool === 'pen' || tool === 'highlighter') return;
                        e.stopPropagation();
                          setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <image 
                        href={s.src || s.url} 
                        width={s.width} 
                        height={s.height} 
                        style={{ cursor: 'move' }}
                        opacity={fadedShapes.has(s.id) ? 0.3 : 1}
                        onError={(e) => {
                          console.error('🔥 Image failed to load:', s.src, e);
                        }}
                        onLoad={() => {
                  
                        }}
                      />
                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <rect
                            x={-8}
                            y={-8}
                            width={s.width + 16}
                            height={s.height + 16}
                            rx={4}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ pointerEvents: 'none' }}
                          />
                          {/* Corner resize handles like Excalidraw */}
                          {[
                            { x: -8, y: -8, cursor: 'nwse-resize' },
                            { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                            { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                            { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                          ].map((corner, i) => (
                            <circle
                              key={i}
                              cx={corner.x}
                              cy={corner.y}
                              r={4}
                              fill="#fff"
                              stroke="#3b82f6"
                              strokeWidth={2}
                              style={{ cursor: corner.cursor }}
                              onMouseDown={(e) => {
                                if (tool === 'pen') return; // Не изменяем размер при рисовании
                                e.stopPropagation();
                                const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                                setIsResizing(true);
                                setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                                setOriginalSize({ width: s.width, height: s.height, x: s.x, y: s.y });
                                setStartShapes([s]);
                              }}
                            />
                          ))}
                        </>
                      )}
                    </g>
                  );
                }
                if (s.type === 'video') {
                  return (
                    <g
                      key={s.id}
                      transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                      onMouseDown={(e) => {
                        if (tool === 'pen') return;
                        e.stopPropagation();
                        setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <VideoPlayer
                        src={s.src}
                        width={s.width}
                        height={s.height}
                        isPlaying={s.isPlaying || false}
                        onPlayPause={(playing) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, isPlaying: playing } : sh))}
                        currentTime={s.currentTime ?? 0}
                        onSeek={(t) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, currentTime: t } : sh))}
                      />
                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <line x1="-8" y1="-8" x2={s.width + 8} y2="-8" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1={s.width + 8} y1="-8" x2={s.width + 8} y2={s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1={s.width + 8} y1={s.height + 8} x2="-8" y2={s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1="-8" y1={s.height + 8} x2="-8" y2="-8" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          {[
                            { x: -8, y: -8, cursor: 'nwse-resize' },
                            { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                            { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                            { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                                                  ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={1 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y });
                              setOriginalSize({ width: s.width, height: s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
                              if (s.type === 'audio') {
                  return (
                    <g 
                      key={s.id} 
                      transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                      onMouseDown={(e) => {
                        if (tool === 'pen') return; // Не захватываем элементы при рисовании
                        e.stopPropagation();
                        setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <AudioPlayer
                        src={s.src}
                        width={s.width}
                        height={s.height}
                        x={0}
                        y={0}
                        onSelect={() => setSelectedShape(s.id)}
                        isSelected={isSelected}
                        onDelete={() => setShapes(prev => prev.filter(shape => shape.id !== s.id))}
                        isPlaying={s.isPlaying || false}
                        onPlayPause={(playing) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, isPlaying: playing } : sh))}
                        currentTime={s.currentTime ?? 0}
                        onSeek={(t) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, currentTime: t } : sh))}
                      />
                      
                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <rect
                            x={-8}
                            y={-8}
                            width={s.width + 16}
                            height={s.height + 16}
                            rx={4}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ pointerEvents: 'none' }}
                          />
                          {/* Corner resize handles */}
                          {[
                            { x: -8, y: -8, cursor: 'nwse-resize' },
                            { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                            { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                            { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                          ].map((corner, i) => (
                            <circle
                              key={i}
                              cx={corner.x}
                              cy={corner.y}
                              r={4 / pan.scale}
                              fill="#fff"
                              stroke="#3b82f6"
                              strokeWidth={1 / pan.scale}
                              style={{ cursor: corner.cursor }}
                              onMouseDown={(e) => {
                                if (tool === 'pen') return; // Не изменяем размер при рисовании
                                e.stopPropagation();
                                const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                                setIsResizing(true);
                                setResizeStart({ x, y });
                                setOriginalSize({ width: s.width, height: s.height });
                                setStartShapes([s]);
                              }}
                            />
                          ))}
                        </>
                      )}
                    </g>
                  );
                }
              if (s.type === 'pdf') {
                  return (
                    <g 
                      key={s.id} 
                      transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                      onMouseDown={(e) => {
                        if (tool === 'pen') return; // Не захватываем элементы при рисовании
                        e.stopPropagation();
                          setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <PDFViewer 
                        pdfUrl={s.pdfUrl}
                        width={s.width}
                        height={s.height}
                        x={0}
                        y={0}
                        onSelect={() => setSelectedShape(s.id)}
                        isSelected={isSelected}
                        tool={tool}
                      />
                      
                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <rect
                            x={-8}
                            y={-8}
                            width={s.width + 16}
                            height={s.height + 16}
                            rx={4}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ pointerEvents: 'none' }}
                          />
                          {/* Corner resize handles like Excalidraw */}
                          {[
                            { x: -8, y: -8, cursor: 'nwse-resize' },
                            { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                            { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                            { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                                                  ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                              r={4}
                            fill="#fff"
                            stroke="#3b82f6"
                              strokeWidth={2}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                                setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                                setOriginalSize({ width: s.width, height: s.height, x: s.x, y: s.y });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'youtube') {
                  return (
                    <g
                      key={s.id}
                      onMouseDown={(e) => {
                        if (tool === 'pen') return;
                        e.stopPropagation();
                          setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                      style={{ cursor: s.isPlaying ? 'pointer' : 'default' }}
                    >
                      <YouTubePlayer
                        videoId={s.videoId}
                        width={s.width}
                        height={s.height}
                        x={s.x}
                        y={s.y}
                        isPlaying={s.isPlaying || false}
                        onVideoClick={() => handleYouTubeClick(s.id)}
                        onSelect={() => setSelectedShape(s.id)}
                        isSelected={isSelected}
                        tool={tool}
                        currentTime={s.currentTime ?? 0}
                        onSeek={(t) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, currentTime: t } : sh))}
                        onPlayPause={(playing) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, isPlaying: playing } : sh))}
                      />

                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <rect
                            x={s.x - 8}
                            y={s.y - 8}
                            width={s.width + 16}
                            height={s.height + 16}
                            rx={4}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ pointerEvents: 'none' }}
                          />
                          {/* Corner resize handles like Excalidraw */}
                          {[
                            { x: s.x - 8, y: s.y - 8, cursor: 'nwse-resize' },
                            { x: s.x + s.width + 8, y: s.y - 8, cursor: 'nesw-resize' },
                            { x: s.x - 8, y: s.y + s.height + 8, cursor: 'nesw-resize' },
                            { x: s.x + s.width + 8, y: s.y + s.height + 8, cursor: 'nwse-resize' }
                          ].map((corner, i) => (
                            <circle
                              key={i}
                              cx={corner.x}
                              cy={corner.y}
                              r={4}
                              fill="#fff"
                              stroke="#3b82f6"
                              strokeWidth={2}
                              style={{ cursor: corner.cursor }}
                              onMouseDown={(e) => {
                                if (tool === 'pen') return;
                                e.stopPropagation();
                                const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                                setIsResizing(true);
                                setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                                setOriginalSize({ width: s.width, height: s.height, x: s.x, y: s.y });
                                setStartShapes([s]);
                              }}
                            />
                          ))}
                        </>
                      )}
                    </g>
                  );
                }
                if (s.type === 'vimeo') {
                  return (
                    <g 
                      key={s.id} 
                      transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                          setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <rect width={s.width} height={s.height} fill={s.type === 'pdf' ? '#e11d48' : '#000'} rx={8} />
                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <line x1="-8" y1="-8" x2={s.width + 8} y2="-8" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1={s.width + 8} y1="-8" x2={s.width + 8} y2={s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1={s.width + 8} y1={s.height + 8} x2="-8" y2={s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1="-8" y1={s.height + 8} x2="-8" y2="-8" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          {[
                            { x: -8, y: -8, cursor: 'nwse-resize' },
                            { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                            { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                            { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                          ].map((corner, i) => (
                            <circle
                              key={i}
                              cx={corner.x}
                              cy={corner.y}
                              r={4 / pan.scale}
                              fill="#fff"
                              stroke="#3b82f6"
                              strokeWidth={1 / pan.scale}
                              style={{ cursor: corner.cursor }}
                                                          onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y });
                              setOriginalSize({ width: s.width, height: s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
                              if (s.type === 'audio') {
                  return (
                    <g 
                      key={s.id} 
                      transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                      onMouseDown={(e) => {
                        if (tool === 'pen') return; // Не захватываем элементы при рисовании
                        e.stopPropagation();
                          setSelectedShape(s.id);
                        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                        setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                      }}
                    >
                      <AudioPlayer 
                        src={s.src}
                        width={s.width}
                        height={s.height}
                        x={0}
                        y={0}
                        onSelect={() => setSelectedShape(s.id)}
                        isSelected={isSelected}
                      />
                      
                      {/* Selection outline */}
                      {isSelected && (
                        <>
                          <line x1="-8" y1="-8" x2={s.width + 8} y2="-8" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1={s.width + 8} y1="-8" x2={s.width + 8} y2={s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1={s.width + 8} y1={s.height + 8} x2="-8" y2={s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          <line x1="-8" y1={s.height + 8} x2="-8" y2="-8" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                          {[
                            { x: -8, y: -8, cursor: 'nwse-resize' },
                            { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                            { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                            { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                          ].map((corner, i) => (
                            <circle
                              key={i}
                              cx={corner.x}
                              cy={corner.y}
                              r={4 / pan.scale}
                              fill="#fff"
                              stroke="#3b82f6"
                              strokeWidth={1 / pan.scale}
                              style={{ cursor: corner.cursor }}
                                                          onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y });
                              setOriginalSize({ width: s.width, height: s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              if (s.type === 'file' || s.type === 'link') {
                const isSelected = s.id === selectedShape;
                
                return (
                  <g 
                    key={s.id} 
                    transform={s.x !== undefined && s.y !== undefined ? `translate(${s.x},${s.y})` : undefined}
                    onMouseDown={(e) => {
                      if (tool === 'pen') return; // Не захватываем элементы при рисовании
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                    onClick={(e) => {
                      if (tool === 'pen') return; // Не открываем при рисовании
                      e.stopPropagation();
                      
                      // Открываем файл или ссылку
                      if (s.type === 'link') {
                        window.open(s.src || s.materialData?.value, '_blank');
                      } else if (s.type === 'file') {
                        // Для файлов открываем в новой вкладке
                        window.open(s.src || s.materialData?.value, '_blank');
                      }
                    }}
                  >
                    {/* Фон для файла/ссылки */}
                    <rect 
                      width={s.width} 
                      height={s.height} 
                      fill="#f8f9fa" 
                      stroke="#dee2e6" 
                      strokeWidth={1} 
                      rx={8}
                      opacity={fadedShapes.has(s.id) ? 0.3 : 1}
                    />
                    
                    {/* Иконка файла/ссылки */}
                    <g transform={`translate(10, ${s.height / 2 - 12})`}>
                      {s.type === 'file' ? (
                        // Иконка файла
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#ee5a24"/>
                          <path d="M14 2V8H20" fill="white"/>
                        </svg>
                      ) : (
                        // Иконка ссылки
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4068 4.45791 20.4791 3.5302C19.5514 2.60249 18.2973 2.07725 16.9864 2.06585C15.6754 2.05445 14.4124 2.55847 13.469 3.469L12.05 4.88" stroke="#ee5a24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 11C13.5705 10.4259 13.0226 9.95085 12.3934 9.60705C11.7643 9.26325 11.0685 9.05889 10.3533 9.00773C9.63821 8.95657 8.92043 9.05975 8.24872 9.31031C7.57701 9.56087 6.96701 9.95302 6.46 10.46L3.46 13.46C2.54921 14.403 2.04519 15.666 2.05659 16.977C2.06799 18.288 2.59323 19.5421 3.52094 20.4698C4.44865 21.3975 5.70272 21.9227 7.0137 21.9341C8.32468 21.9455 9.58769 21.4415 10.531 20.531L11.95 19.12" stroke="#ee5a24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </g>
                    
                    {/* Название файла/ссылки */}
                    <text 
                      x={40} 
                      y={s.height / 2 + 4} 
                      fontSize="14" 
                      fill="#333" 
                      style={{ 
                        fontFamily: 'Arial, sans-serif',
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      {s.name || s.materialData?.name || 'Untitled'}
                    </text>
                    
                    {/* Selection outline */}
                    {isSelected && (
                      <>
                        <rect
                          x={-8}
                          y={-8}
                          width={s.width + 16}
                          height={s.height + 16}
                          rx={4}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* Corner resize handles like Excalidraw */}
                        {[
                          { x: -8, y: -8, cursor: 'nwse-resize' },
                          { x: s.width + 8, y: -8, cursor: 'nesw-resize' },
                          { x: -8, y: s.height + 8, cursor: 'nesw-resize' },
                          { x: s.width + 8, y: s.height + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return; // Не изменяем размер при рисовании
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y, corner: ['nw', 'ne', 'sw', 'se'][i] });
                              setOriginalSize({ width: s.width, height: s.height, x: s.x, y: s.y });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
            }
              if (s.type === 'sticker') {
                const isSelected = s.id === selectedShape;
                const isEditing = s.id === editingStickerId;

                return (
                  <g key={s.id}>
                    {/* Стикер без рамки, только мягкая тень */}
                    <foreignObject x={s.x} y={s.y} width={s.w} height={s.h} style={{overflow:'visible'}} className="sticker-foreign">
                      <div
                        ref={isEditing ? stickerEditRef : null}
                        data-shape-id={s.id}
                        style={{
                          width: s.w,
                          height: s.h,
                          background: s.color === 'transparent' ? 'none' : s.color,
                          border: 'none',
                          borderRadius: 0,
                          color: s.styles?.color || s.textColor,
                          fontWeight: s.styles?.fontWeight || 600,
                          fontSize: s.styles?.fontSize || '18px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          padding: 8,
                          textAlign: s.styles?.textAlign || 'center',
                          cursor: isEditing ? 'text' : draggedSticker === s.id ? 'grabbing' : 'pointer',
                          outline: 'none',
                          userSelect: isEditing ? 'text' : 'none',
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          overflow: 'hidden',
                          fontFamily: s.styles?.fontFamily || 'Arial, sans-serif',
                          textShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: fadedShapes.has(s.id) ? 0.3 : 1,
                          fontStyle: s.styles?.fontStyle || 'normal',
                          textDecoration: s.styles?.textDecoration || 'none',
                        }}
                        tabIndex={0}
                        onClick={e => { 
                          setSelectedShape(s.id); 
                        }}
                        onDoubleClick={e => { 
                          setSelectedShape(s.id); 
                          setEditingStickerId(s.id); 
                        }}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={e => {
                          if (e.target && e.target.innerHTML !== undefined) {
                          // Очищаем HTML теги, но сохраняем пробелы и позицию
                          const tempDiv = document.createElement('div');
                          tempDiv.innerHTML = e.target.innerHTML;
                          const newText = tempDiv.textContent || tempDiv.innerText || '';
                          // Сохраняем текущие стили стикера
                          const currentStyles = {
                            fontWeight: e.target.style.fontWeight || '600',
                            fontStyle: e.target.style.fontStyle || 'normal',
                            textDecoration: e.target.style.textDecoration || 'none',
                            color: e.target.style.color || s.textColor,
                            fontSize: e.target.style.fontSize || '18px',
                            textAlign: e.target.style.textAlign || 'center',
                            fontFamily: e.target.style.fontFamily || 'Arial, sans-serif'
                          };
                          setShapes(prev => prev.map(sh => sh.id === s.id ? { 
                            ...sh, 
                            text: newText,
                            styles: currentStyles
                          } : sh));
                          }
                          setEditingStickerId(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            // Явно создаем новую строку в стикерах
                            e.preventDefault();
                            document.execCommand('insertLineBreak', false, null);
                          }
                          if (isEditing && (e.key === 'Delete' || e.key === 'Backspace')) {
                            e.stopPropagation();
                          }
                          // Разрешаем все остальные клавиши, включая пробел
                          // Не предотвращаем стандартное поведение для пробела
                        }}


                        onMouseDown={e => {
                          if (tool === 'pen') return; // Не захватываем элементы при рисовании
                          // Убираем e.stopPropagation() чтобы события обрабатывались через общую систему
                          setSelectedShape(s.id);
                          const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                          setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                        }}
                        dangerouslySetInnerHTML={{ __html: s.text }}
                      >
                      </div>
                    </foreignObject>
                    {/* Только синяя рамка и маркеры при выделении */}
                    {isSelected && (
                      <>
                        {/* Single blue line with rounded ends - exactly like YouTube */}
                        <line x1={s.x - 12} y1={s.y - 12} x2={s.x + s.w + 27} y2={s.y - 12} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        <line x1={s.x + s.w + 27} y1={s.y - 12} x2={s.x + s.w + 27} y2={s.y + s.h + 27} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        <line x1={s.x + s.w + 27} y1={s.y + s.h + 27} x2={s.x - 12} y2={s.y + s.h + 27} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        <line x1={s.x - 12} y1={s.y + s.h + 27} x2={s.x - 12} y2={s.y - 12} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        
                        {/* Corner resize handles - exactly like YouTube */}
                        {[
                          { x: s.x - 12, y: s.y - 12, cursor: 'nwse-resize' },
                          { x: s.x + s.w + 27, y: s.y - 12, cursor: 'nesw-resize' },
                          { x: s.x - 12, y: s.y + s.h + 27, cursor: 'nesw-resize' },
                          { x: s.x + s.w + 27, y: s.y + s.h + 27, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={1 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
                              const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
                              setResizing({ shape: s, startX: cx, startY: cy, startW: s.w, startH: s.h, startX0: s.x, startY0: s.y, corner: [corner.x === s.x - 12 ? 0 : 1, corner.y === s.y - 12 ? 0 : 1] });
                            }}
                            onPointerDown={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              setResizing({ shape: s, startX: e.clientX, startY: e.clientY, startW: s.w, startH: s.h, startX0: s.x, startY0: s.y, corner: [corner.x === s.x - 12 ? 0 : 1, corner.y === s.y - 12 ? 0 : 1] });
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              
              // Handle YouTube videos
              if (s.type === 'youtube') {
                const isSelected = s.id === selectedShape;

                return (
                  <g
                    key={s.id}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setSelectedShape(s.id);
                      const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                      setDragged({ id: s.id, offsetX: x - s.x, offsetY: y - s.y });
                    }}
                    style={{ cursor: s.isPlaying ? 'pointer' : 'default' }}
                  >
                    <YouTubePlayer
                      videoId={s.videoId}
                      width={s.width}
                      height={s.height}
                      x={s.x}
                      y={s.y}
                      isPlaying={s.isPlaying || false}
                      onVideoClick={() => handleYouTubeClick(s.id)}
                      onSelect={() => setSelectedShape(s.id)}
                      isSelected={isSelected}
                      tool={tool}
                      currentTime={s.currentTime ?? 0}
                      onSeek={(t) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, currentTime: t } : sh))}
                      onPlayPause={(playing) => setShapes(prev => prev.map(sh => sh.id === s.id ? { ...sh, isPlaying: playing } : sh))}
                    />

                    {/* Selection outline */}
                    {isSelected && (
                      <>
                        {/* Single blue line with rounded ends */}
                        <line x1={s.x - 8} y1={s.y - 8} x2={s.x + s.width + 8} y2={s.y - 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        <line x1={s.x + s.width + 8} y1={s.y - 8} x2={s.x + s.width + 8} y2={s.y + s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        <line x1={s.x + s.width + 8} y1={s.y + s.height + 8} x2={s.x - 8} y2={s.y + s.height + 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />
                        <line x1={s.x - 8} y1={s.y + s.height + 8} x2={s.x - 8} y2={s.y - 8} stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" />

                        {/* Corner resize handles */}
                        {[
                          { x: s.x - 8, y: s.y - 8, cursor: 'nwse-resize' },
                          { x: s.x + s.width + 8, y: s.y - 8, cursor: 'nesw-resize' },
                          { x: s.x - 8, y: s.y + s.height + 8, cursor: 'nesw-resize' },
                          { x: s.x + s.width + 8, y: s.y + s.height + 8, cursor: 'nwse-resize' }
                        ].map((corner, i) => (
                          <circle
                            key={i}
                            cx={corner.x}
                            cy={corner.y}
                            r={4 / pan.scale}
                            fill="#fff"
                            stroke="#3b82f6"
                            strokeWidth={1 / pan.scale}
                            style={{ cursor: corner.cursor }}
                            onMouseDown={(e) => {
                              if (tool === 'pen') return;
                              e.stopPropagation();
                              const { x, y } = getPointerCoords(e, panRef.current, svgRef);
                              setIsResizing(true);
                              setResizeStart({ x, y });
                              setOriginalSize({ width: s.width, height: s.height });
                              setStartShapes([s]);
                            }}
                          />
                        ))}
                      </>
                    )}
                  </g>
                );
              }
              
              return null;
            })}
    </>
  );
};

export default WhiteboardShapeList;
